import { useEffect, useMemo, useState } from "react"
import { Button, Form } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import isEmail from "validator/lib/isEmail"

import ControlInput from "../../../components/form-inputs/ControlInput"
import AccountTypeInput from "../components/AccountTypeInput"
import LoadingTextSwap from "../../../components/LoadingTextSwap"
import { isValidAccountType, isValidAddress, isValidCityOrCounty, isValidCountry, isValidDob, isValidName, isValidPassword, isValidPhoneNumber, isValidPostcode } from "../../../utils/validateInput"
import ClubNumberInput from "../components/ClubNumberInput"
import { useCheckEmailExistsMutation } from "../../../app/authApiSlice"
import { useFetchClubsQuery } from "../../../app/clubsApiSlice"
import { useMigrateAccountMutation } from "../../../app/adminApiSlice"
import calculateIsUnder16 from "../../../utils/calculateIsUnder16"

const CreateAccountPage = () => {
  const navigate = useNavigate()
  const { data: clubsList } = useFetchClubsQuery()
  const [checkEmailExists] = useCheckEmailExistsMutation()
  const [createAccount] = useMigrateAccountMutation()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [dob, setDob] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [streetAddress, setStreetAddress] = useState("")
  const [city, setCity] = useState("")
  const [county, setCounty] = useState("")
  const [postcode, setPostcode] = useState("")
  const [country, setCountry] = useState("")
  const [accountType, setAccountType] = useState("user")
  const [clubNumber, setClubNumber] = useState("")
  const [newsletter, setNewsletter] = useState(false)
  const [parentFirstName, setParentFirstName] = useState("")
  const [parentLastName, setParentLastName] = useState("")
  const [parentPhoneNumber, setParentPhoneNumber] = useState("")
  const [parentEmail, setParentEmail] = useState("")

  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [blurAll, setBlurAll] = useState(false) // To trigger all inputs to show validation errors when trying to submit with empty fields

  const [valid, setValid] = useState({
    password: false,
    passwordConfirm: false,
    email: false,
    firstName: false,
    lastName: false,
    dob: false,
    streetAddress: false,
    city: false,
    county: false,
    postcode: false,
    country: false,
    phoneNumber: false,
    accountType: false,
  })

  const [parentValid, setParentValid] = useState({
    parentFirstName: false,
    parentLastName: false,
    parentPhoneNumber: false,
    parentEmail: false,
  })

  // Function to update valid state
  const updateValid = (key, value) => {
    setValid(prev => ({ ...prev, [key]: value }))
  }

  // Function to update parent valid state
  const updateParentValid = (key, value) => {
    setParentValid(prev => ({ ...prev, [key]: value }))
  }

  // Special handler for password to also validate passwordConfirm if it has a value
  const handleSetPassword = (value) => {
    setPassword(value)
    if (value !== passwordConfirm) {
      updateValid("passwordConfirm", false)
    } else {
      updateValid("passwordConfirm", true)
    }
  }

  // Check if user is under 16 and memoize to avoid recalculating on every render
  const isUnder16 = useMemo(() => calculateIsUnder16(dob), [dob])

  // Parent data is only required to be valid if user is under 16
  const parentDataValid = isUnder16
    ? Object.values(parentValid).every(Boolean)
    : true // If 16+, parent data doesn't matter

  const allValid = Object.values(valid).every(Boolean) && parentDataValid

  // Clear error message when certain fields are updated
  useEffect(() => {
    if (error && allValid) {
      setError("")
    }
  }, [allValid])

  // Clear email error message when email is updated
  useEffect(() => {
    if (error === "Email already in use") {
      setError("")
    }
  }, [email])

  const handleSubmit = async e => {
    e.preventDefault()

    if (error || loading) return

    if (!allValid) {
      setBlurAll(true)
      return setError("All fields must be valid")
    }

    setLoading(true)

    // Check if email is already in use
    const { emailExists } = await checkEmailExists(email).unwrap()

    if (emailExists) return setError("Email already in use"), setLoading(false)

    // If club number is provided, check if it exists. If clubsList is not available dont worry because backend does the check aswell.
    if (clubNumber && clubsList) {
      const clubExists = clubNumber in clubsList

      if (!clubExists) {
        setError("Club does not exist")
        setLoading(false)
        return
      }
    }

    // Create user account
    const createAccountData = {
      email,
      password
    }

    const completeMembershipData = {
      email,
      firstName,
      lastName,
      dob,
      phoneNumber,
      address: {
        streetAddress,
        city,
        county,
        postcode,
        country,
      },
      accountType,
      clubNumber,
      newsletter,
      parentFirstName,
      parentLastName,
      parentPhoneNumber,
      parentEmail
    }

    try {
      await createAccount({ createAccountData, completeMembershipData }).unwrap()

      toast.success("Account created")
      navigate("/admin/members")
    } catch (error) {
      console.log(error)
      toast.error("Error creating account")
    }


    setLoading(false)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Email</b>
        <div className="w-100">
          <ControlInput
            type="email"
            name="email"
            value={email}
            isValid={valid.email}
            setValid={updateValid}
            setValue={setEmail}
            validTest={isEmail}
            blurAll={blurAll}
            feedback="Please enter a valid email address"
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Password</b>
        <div className="w-100">
          <ControlInput
            type="password"
            name="password"
            value={password}
            isValid={valid.password}
            setValid={updateValid}
            setValue={handleSetPassword}
            validTest={isValidPassword}
            blurAll={blurAll}
            feedback="Password must be at least 8 characters long and contain at least one number, one uppercase letter, one lowercase letter and one special character"
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Confirm Password</b>
        <div className="w-100">
          <ControlInput
            type="password"
            name="passwordConfirm"
            value={passwordConfirm}
            isValid={valid.passwordConfirm}
            setValid={updateValid}
            setValue={setPasswordConfirm}
            validTest={(newPasswordConfirm) => password === newPasswordConfirm}
            blurAll={blurAll}
            feedback="Passwords do not match"
          />
        </div>
      </div>
      <div className='border-white'>
        <div colSpan={2} className='p-0 ps-2'>
          <p className="mb-3 mt-5 fw-bold h4">Personal Details</p>
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">First Name</b>
        <div className="w-100">
          <ControlInput
            type="text"
            name="firstName"
            value={firstName}
            isValid={valid.firstName}
            setValid={updateValid}
            setValue={setFirstName}
            validTest={isValidName}
            blurAll={blurAll}
            feedback="Please enter a valid first name"
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Last Name</b>
        <div className="w-100">
          <ControlInput
            type="text"
            name="lastName"
            value={lastName}
            isValid={valid.lastName}
            setValid={updateValid}
            setValue={setLastName}
            validTest={isValidName}
            blurAll={blurAll}
            feedback="Please enter a valid last name"
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Date of Birth</b>
        <div className="w-100">
          <ControlInput
            type="date"
            name="dob"
            value={dob}
            isValid={valid.dob}
            setValid={updateValid}
            setValue={setDob}
            validTest={isValidDob}
            blurAll={blurAll}
            feedback="Please enter a valid date of birth"
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Phone Number</b>
        <div className="w-100">
          <ControlInput
            type="tel"
            name="phoneNumber"
            value={phoneNumber}
            isValid={valid.phoneNumber}
            setValid={updateValid}
            setValue={setPhoneNumber}
            blurAll={blurAll}
            validTest={isValidPhoneNumber}

            feedback="Please enter a valid phone number"
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row  border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Address</b>
        <div className="w-100 d-flex flex-column gap-2">
          <ControlInput
            type="text"
            name="streetAddress"
            value={streetAddress}
            isValid={valid.streetAddress}
            setValid={updateValid}
            setValue={setStreetAddress}
            validTest={isValidAddress}
            blurAll={blurAll}
            placeholder="Street Address"
            feedback="Please enter a valid street address"
          />
          <ControlInput
            type="text"
            name="city"
            value={city}
            isValid={valid.city}
            setValid={updateValid}
            setValue={setCity}
            validTest={isValidCityOrCounty}
            blurAll={blurAll}
            feedback="Please enter a valid city"
            placeholder="City"
          />
          <ControlInput
            type="text"
            name="county"
            value={county}
            isValid={valid.county}
            setValid={updateValid}
            setValue={setCounty}
            validTest={isValidCityOrCounty}
            blurAll={blurAll}
            feedback="Please enter a valid county"
            placeholder="County"
          />
          <ControlInput
            type="text"
            name="postcode"
            value={postcode}
            isValid={valid.postcode}
            setValid={updateValid}
            setValue={setPostcode}
            validTest={isValidPostcode}
            blurAll={blurAll}
            feedback="Please enter a valid postcode"
            placeholder="Postcode"
          />
          <ControlInput
            type="text"
            name="country"
            value={country}
            isValid={valid.country}
            setValid={updateValid}
            setValue={setCountry}
            validTest={isValidCountry}
            blurAll={blurAll}
            feedback="Please enter a valid country"
            placeholder="Country"
          />
        </div>
      </div>

      {/* Parent/Guardian Information */}
      {(isUnder16 && valid.dob) && (
        <>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Parent/Guardian First Name</b>
            <div className="w-100">
              <ControlInput
                type="text"
                name="parentFirstName"
                value={parentFirstName}
                isValid={parentValid.parentFirstName}
                setValid={updateParentValid}
                setValue={setParentFirstName}
                validTest={isValidName}
                blurAll={blurAll}
                feedback="Please enter a valid first name"
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Parent/Guardian Last Name</b>
            <div className="w-100">
              <ControlInput
                type="text"
                name="parentLastName"
                value={parentLastName}
                isValid={parentValid.parentLastName}
                setValid={updateParentValid}
                setValue={setParentLastName}
                validTest={isValidName}
                blurAll={blurAll}
                feedback="Please enter a valid last name"
              />
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Parent/Guardian Phone Number</b>
            <div className="w-100">
              <ControlInput
                type="tel"
                name="parentPhoneNumber"
                value={parentPhoneNumber}
                isValid={parentValid.parentPhoneNumber}
                setValid={updateParentValid}
                setValue={setParentPhoneNumber}
                validTest={isValidPhoneNumber}
                blurAll={blurAll}
                feedback="Please enter a valid phone number"
              />
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
            <b className="text-muted w-100 my-1">Parent/Guardian Email</b>
            <div className="w-100">
              <ControlInput
                type="email"
                name="parentEmail"
                value={parentEmail}
                isValid={parentValid.parentEmail}
                setValid={updateParentValid}
                setValue={setParentEmail}
                validTest={isEmail}
                blurAll={blurAll}
                feedback="Please enter a valid email address"
              />
            </div>
          </div>
        </>
      )}

      <div className='border-white'>
        <div colSpan={2} className='p-0 ps-2'>
          <h4 className="mb-3 mt-5 fw-bold">Account Details</h4>
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Account Type</b>
        <div className="w-100">
          <AccountTypeInput
            name="accountType"
            isValid={valid.accountType}
            setValid={updateValid}
            setValue={setAccountType}
            value={accountType}
            blurAll={blurAll}
            validTest={isValidAccountType}
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
        <b className="text-muted w-100 my-1">Club Number (optional)</b>
        <div className="w-100">
          <ClubNumberInput
            setValue={setClubNumber}
            value={clubNumber}
            setError={setError}
          />
        </div>
      </div>
      <div className='border-white'>
        <div colSpan={2} className='p-0 ps-2'>
          <Form.Group className=" mt-4 mb-3 text-center" controlId="newsletter">
            <Form.Check
              type="checkbox"
              label="Sign up to newsletter"
              checked={newsletter}
              onChange={(e) => setNewsletter(e.target.checked)}
              className="d-inline-block fw-semibold text-muted"
            />
          </Form.Group>
        </div>
      </div>
      {error && <p className="text-danger i text-center fw-semibold">{error}</p>}
      <Button
        disabled={loading}
        className="px-4 mt-2 mx-auto rounded-3 d-block"
        variant="dark"
        type="submit"
      >
        <LoadingTextSwap isLoading={loading}>
          Create Account
        </LoadingTextSwap>
      </Button>
    </Form>
  )
}

export default CreateAccountPage